import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Button, Pagination, Table } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiEdit, FiFileText, FiSearch, FiX, FiTrash2 } from "react-icons/fi";
import Select from "react-select";
import Swal from "sweetalert2";

export default function SaleIndex() {
    const { t } = useContext(TranslatorContext);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const role = localStorage.getItem("role");
    const isAdmin = role?.toLowerCase() === 'admin';

    // Filter states
    const today = new Date().toISOString().split("T")[0];

    const [tempFilters, setTempFilters] = useState({
        customer_id: searchParams.get('customer_id') || "",
        sale_number: searchParams.get('sale_number') || "",
        status: searchParams.get('status') || "",
        start_date: searchParams.get('start_date') || today,
        end_date: searchParams.get('end_date') || today,
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [activeFilters, setActiveFilters] = useState({
        customer_id: searchParams.get('customer_id') || "",
        sale_number: searchParams.get('sale_number') || "",
        status: searchParams.get('status') || "",
        start_date: searchParams.get('start_date') || today,
        end_date: searchParams.get('end_date') || today,
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [customers, setCustomers] = useState([]);
    const [showFilters, setShowFilters] = useState(isAdmin);
    const [applyingFilter, setApplyingFilter] = useState(false);

    // Fetch sales with filters
    const fetchSales = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            // Build query params
            const params = new URLSearchParams();
            if (activeFilters.customer_id) params.append('customer_id', activeFilters.customer_id);
            if (activeFilters.sale_number) params.append('sale_number', activeFilters.sale_number);
            if (activeFilters.status) params.append('status', activeFilters.status);
            if (activeFilters.start_date) params.append('start_date', activeFilters.start_date);
            if (activeFilters.end_date) params.append('end_date', activeFilters.end_date);
            if (activeFilters.per_page) params.append('per_page', activeFilters.per_page);
            if (activeFilters.page) params.append('page', activeFilters.page);

            const url = `${process.env.REACT_APP_API_URL}/sales${params.toString() ? `?${params.toString()}` : ''}`;

            const res = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const customersData = res.data.customers?.data || res.data.customers || [];
            setCustomers(customersData);

            const salesData = res.data.sales;

            if (salesData && typeof salesData === 'object' && salesData.data) {
                setSales(salesData.data);
                setPagination({
                    current_page: salesData.current_page || 1,
                    last_page: salesData.last_page || 1,
                    per_page: salesData.per_page || 10,
                    total: salesData.total || 0,
                    from: salesData.from || 0,
                    to: salesData.to || 0
                });
            } else if (Array.isArray(salesData)) {
                setSales(salesData);
                setPagination({
                    current_page: 1,
                    last_page: 1,
                    per_page: salesData.length,
                    total: salesData.length,
                    from: 1,
                    to: salesData.length
                });
            } else {
                setSales([]);
            }
        } catch (err) {
            console.error("Error fetching sales:", err);
        } finally {
            setLoading(false);
        }
    }, [activeFilters]);

    useEffect(() => {
        fetchSales();
    }, [fetchSales]);

    // Handle filter change
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setTempFilters(prev => ({ ...prev, [name]: value }));
    };

    // Handle customer select change
    const handleCustomerChange = (selectedOption) => {
        setTempFilters(prev => ({
            ...prev,
            customer_id: selectedOption ? selectedOption.value : ""
        }));
    };

    // Apply filters
    const applyFilters = () => {
        setActiveFilters({
            ...tempFilters,
            page: 1
        });

        const params = new URLSearchParams();
        if (tempFilters.customer_id) params.append('customer_id', tempFilters.customer_id);
        if (tempFilters.sale_number) params.append('sale_number', tempFilters.sale_number);
        if (tempFilters.status) params.append('status', tempFilters.status);
        if (tempFilters.start_date) params.append('start_date', tempFilters.start_date);
        if (tempFilters.end_date) params.append('end_date', tempFilters.end_date);
        if (tempFilters.per_page && tempFilters.per_page !== 10) params.append('per_page', tempFilters.per_page);
        if (tempFilters.page && tempFilters.page !== 1) params.append('page', 1);
        setSearchParams(params);

        setApplyingFilter(true);
        setTimeout(() => setApplyingFilter(false), 500);
    };

    // Reset filters
    const resetFilters = () => {
        const emptyFilters = {
            customer_id: "",
            sale_number: "",
            status: "",
            start_date: today,
            end_date: today,
            per_page: 10,
            page: 1
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setSearchParams({});
        setTimeout(() => fetchSales(), 100);
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setActiveFilters(prev => ({ ...prev, page: page }));
        }
    };

    // Status badge
    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case "paid":
                return "bg-success";
            case "partial":
                return "bg-info";
            case "pending":
                return "bg-warning";
            case "unpaid":
                return "bg-danger";
            default:
                return "bg-secondary";
        }
    };

    // Get status text
    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case "paid":
                return t("Paid");
            case "partial":
                return t("Partial");
            case "pending":
                return t("Pending");
            case "unpaid":
                return t("Unpaid");
            default:
                return status || t("Unknown");
        }
    };

    // Prepare customer options
    const customerOptions = customers.map(customer => ({
        value: customer.id,
        label: customer.name
    }));

    const handleDeleteSale = async (saleId, saleNumber) => {
        const result = await Swal.fire({
            title: t('Are you sure?'),
            text: t(`You want to delete sale ${saleNumber}?`),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                // Use DELETE method instead of POST
                const response = await axios.delete(
                    `${process.env.REACT_APP_API_URL}/sale-destroy/${saleId}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.data.status === true || response.data.success === true) {
                    Swal.fire({
                        title: t('Deleted!'),
                        text: t('Sale has been deleted.'),
                        icon: 'success',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 2000
                    });
                    fetchSales();
                } else {
                    Swal.fire({
                        title: t('Error!'),
                        text: response.data.message || t('Failed to delete sale'),
                        icon: 'error'
                    });
                }
            } catch (error) {
                console.error("Delete error:", error);
                Swal.fire({
                    title: t('Error!'),
                    text: error.response?.data?.message || t('Something went wrong'),
                    icon: 'error'
                });
            }
        }
    };

    const selectedCustomer = customerOptions.find(option => option.value === parseInt(tempFilters.customer_id));

    if (loading && sales.length === 0) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </div>
                    <p className="mt-3">{t("Loading sales...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('sales')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('sales')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('sale list')}</h5>
                            {hasPermission("Sales create") && (
                                <ButtonComponent
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => navigate("/sale-create")}
                                    text={t("Create New Sale")}
                                />
                            )}
                        </div>

                        {/* Filter Section */}
                        {showFilters && (
                            <div className="p-3 border-bottom bg-light">
                                <Row className="g-3">
                                    <Col xs={12} sm={6} md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">{t("Sale Number")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="sale_number"
                                                value={tempFilters.sale_number}
                                                onChange={handleFilterChange}
                                                placeholder={t("sale number")}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={6} md={3}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">{t("Customer")}</Form.Label>
                                            <Select
                                                options={customerOptions}
                                                value={selectedCustomer}
                                                onChange={handleCustomerChange}
                                                isClearable
                                                isSearchable
                                                placeholder={t("Select customer...")}
                                                noOptionsMessage={() => t("No customers found")}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                styles={{
                                                    control: (provided) => ({ ...provided, minHeight: '45px' }),
                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">{t("Status")}</Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={tempFilters.status}
                                                onChange={handleFilterChange}
                                                size="sm"
                                            >
                                                <option value="">{t('All Status')}</option>
                                                <option value="paid">{t('Paid')}</option>
                                                <option value="partial">{t('Partial')}</option>
                                                <option value="unpaid">{t('Unpaid')}</option>
                                                <option value="pending">{t('Pending')}</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">{t("Start Date")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="start_date"
                                                value={tempFilters.start_date}
                                                onChange={handleFilterChange}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} sm={6} md={2}>
                                        <Form.Group>
                                            <Form.Label className="small fw-bold">{t("End Date")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="end_date"
                                                value={tempFilters.end_date}
                                                onChange={handleFilterChange}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12}>
                                        <div className="d-flex gap-2 justify-content-end">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={resetFilters}
                                                className="d-flex align-items-center gap-1"
                                            >
                                                <FiX size={14} /> {t("Reset")}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={applyFilters}
                                                disabled={applyingFilter}
                                                className="d-flex align-items-center gap-1"
                                            >
                                                {applyingFilter ? (
                                                    <>
                                                        <div className="spinner-border spinner-border-sm" role="status" />
                                                        <span>{t("Applying...")}</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <FiSearch size={14} /> {t("Search")}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )}

                        {/* Results Info */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white flex-wrap gap-2">
                            <div className="text-muted small">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("entries")}
                            </div>
                            {loading && (
                                <div className="text-muted small">
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </div>
                            )}
                        </div>

                        {/* Responsive Table */}
                        <div className="table-responsive-wrapper">
                            <Table responsive hover className="table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '15%' }}>{t('Sale Number')}</th>
                                        <th style={{ width: '10%' }}>{t('Entered By')}</th>
                                        <th style={{ width: '10%' }}>{t('Sale Date')}</th>
                                        <th style={{ width: '15%' }}>{t('Customer')}</th>
                                        <th style={{ width: '25%' }}>{t('Product')}</th>
                                        <th className="text-end" style={{ width: '8%' }}>{t('Paid Amount')}</th>
                                        <th className="text-center" style={{ width: '15%' }}>{t('Status')}</th>
                                        <th className="text-center" style={{ width: '5%' }}>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.length > 0 ? (
                                        sales.map((sale, index) => {
                                            const paidAmount = Number(sale.paid_amount ?? 0);

                                            const hasMergedPayment =
                                                sale.payments?.some(payment => payment.self_payment_id !== null);

                                            const hasAnyPayment =
                                                sale.payments && sale.payments.length > 0;
                                            const status = sale.status || "pending";

                                            const exchange = sale.exchange || null;
                                            const exchangeLinks = exchange?.linked_sale_numbers || [];

                                            return (
                                                <tr key={sale.id}>
                                                    <td className="text-center">
                                                        {sale.id || '-'}
                                                    </td>
                                                    <td>
                                                        <strong >{sale.sale_number || '-'}</strong>
                                                        {exchange && (
                                                            <div className="small mt-1">
                                                                <span
                                                                    className={`badge ${exchange.role === 'exchange_sale'
                                                                        ? 'bg-info text-dark'
                                                                        : 'bg-warning text-dark'}`}
                                                                >
                                                                    {exchange.role === 'exchange_sale'
                                                                        ? t('Exchange Sale')
                                                                        : t('Exchanged')}
                                                                </span>
                                                                {exchangeLinks.length > 0 && (
                                                                    <span className="text-muted ms-1">
                                                                        {exchange.role === 'exchange_sale'
                                                                            ? t('from')
                                                                            : t('to')}{' '}
                                                                        {exchangeLinks.join(', ')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{sale.entered_by || '-'}</td>
                                                    <td>{sale.sale_date}</td>
                                                    <td>{sale.customer_name || '-'}</td>
                                                    <td>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {sale.sale_summary?.slice(0, 3).map((item, i) => (
                                                                <span key={i} className="badge bg-light text-dark">
                                                                    {item.name} × {item.qty}
                                                                </span>
                                                            ))}
                                                            {sale.sale_summary?.length > 3 && (
                                                                <span className="badge bg-secondary">
                                                                    +{sale.sale_summary.length - 3} more
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-end fw-bold">
                                                        {paidAmount > 0
                                                            ? paidAmount.toLocaleString()
                                                            : hasMergedPayment || exchange?.credit_in > 0
                                                                ? "0"
                                                                : hasAnyPayment
                                                                    ? "0"
                                                                    : "-"}
                                                        {exchange?.credit_in > 0 && (
                                                            <div className="small fw-normal text-info">
                                                                + {Number(exchange.credit_in).toLocaleString()}{' '}
                                                                {t('credit carried')}
                                                            </div>
                                                        )}
                                                        {exchange?.credit_out > 0 && (
                                                            <div className="small fw-normal text-warning">
                                                                - {Number(exchange.credit_out).toLocaleString()}{' '}
                                                                {t('credit moved')}
                                                            </div>
                                                        )}
                                                        {exchange?.refunded > 0 && (
                                                            <div className="small fw-normal text-danger">
                                                                - {Number(exchange.refunded).toLocaleString()}{' '}
                                                                {t('refunded')}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span className={`badge ${getStatusBadgeClass(status)}`}>
                                                                {getStatusText(status)}
                                                            </span>

                                                            {sale.appointment_number && (
                                                                <small className="text-muted mt-1">
                                                                    {sale.appointment_number}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="mc-table-action justify-content-center">
                                                            {hasPermission("Sales edit") && (
                                                                <ButtonComponent
                                                                    type="button"
                                                                    className="material-icons edit"
                                                                    onClick={() => navigate(`/sale-edit/${sale.id}`)}
                                                                    title={t("Edit")}
                                                                >
                                                                    <FiEdit />
                                                                </ButtonComponent>
                                                            )}

                                                            {isAdmin && (
                                                                <ButtonComponent
                                                                    type="button"
                                                                    className="material-icons delete"
                                                                    onClick={() => handleDeleteSale(sale.id, sale.sale_number)}
                                                                    title={t("Delete")}
                                                                >
                                                                    <FiTrash2 />
                                                                </ButtonComponent>
                                                            )}
                                                            <ButtonComponent
                                                                type="button"
                                                                className="material-icons view"
                                                                onClick={() =>
                                                                    window.open(
                                                                        `${process.env.REACT_APP_URL}/sale/print/${sale.id}`,
                                                                        "_blank"
                                                                    )
                                                                }
                                                                title={t("Print")}
                                                            >
                                                                <FiFileText />
                                                            </ButtonComponent>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="text-center py-5">
                                                <div className="empty-state">
                                                    <p className="mb-2">{t("No sales found")}</p>
                                                    {Object.values(activeFilters).some(v => v && v !== 1 && v !== 10) && (
                                                        <Button
                                                            variant="link"
                                                            onClick={resetFilters}
                                                            className="text-primary"
                                                        >
                                                            {t("Clear filters")}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                </div>
                                <Pagination className="mb-0 flex-wrap justify-content-center">
                                    <Pagination.First
                                        onClick={() => handlePageChange(1)}
                                        disabled={pagination.current_page === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    />

                                    {(() => {
                                        const pages = [];
                                        const maxPages = Math.min(5, pagination.last_page);
                                        let startPage = Math.max(1, pagination.current_page - 2);
                                        let endPage = Math.min(pagination.last_page, startPage + maxPages - 1);

                                        if (endPage - startPage + 1 < maxPages) {
                                            startPage = Math.max(1, endPage - maxPages + 1);
                                        }

                                        for (let i = startPage; i <= endPage; i++) {
                                            pages.push(
                                                <Pagination.Item
                                                    key={i}
                                                    active={i === pagination.current_page}
                                                    onClick={() => handlePageChange(i)}
                                                >
                                                    {i}
                                                </Pagination.Item>
                                            );
                                        }
                                        return pages;
                                    })()}

                                    <Pagination.Next
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                    <Pagination.Last
                                        onClick={() => handlePageChange(pagination.last_page)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}